import type {
  SearchFilterSelection,
  SearchFilterType,
  SearchPagination,
  SearchSorting,
} from '@/scopes/search/search/infrastructure/api/types'
import { createStore, type StoreApi } from 'zustand'

import { DEFAULT_RESULTS_PER_PAGE } from '@/scopes/search/search/domain/constants'

export interface SearchState {
  pagination: SearchPagination
  sorting: SearchSorting
  filterSelection: SearchFilterSelection
  aiFilterSelection: SearchFilterSelection
  userQuery: string
  isAIFetching: boolean
  isAIError: boolean
  initialAccommodationIds?: string[]
}

export interface SearchStoreActions {
  setPage: (pageNumber: number | string) => void
  setNextPage: () => void
  setPreviousPage: () => void
  setSortingBy: (by: string) => void
  setFilterSelection: <K extends SearchFilterType>(filterType: K, value: SearchFilterSelection[K]) => void
  setParticipantsAndSorting: (participants: SearchFilterSelection['Participants'], sortingBy: string) => void
  resetFilterValues: <K extends SearchFilterType>(filterTypesToReset: K[]) => void
  setUserQuery: (userQuery: string) => void
  setIsAIFetching: (isAIFetching: boolean) => void
  setIsAIError: (isAIError: boolean) => void
  clearInitialAccommodationIds: () => void
}

export type SearchStore = SearchState & SearchStoreActions

const initialState = {
  pagination: {
    offset: 0,
    limit: DEFAULT_RESULTS_PER_PAGE,
  },
  sorting: {
    by: 'Popularity',
  },
  filterSelection: {},
  aiFilterSelection: {},
  userQuery: '',
  isAIFetching: false,
  isAIError: false,
}

export type SearchStoreApi = Pick<StoreApi<SearchStore>, 'getState'>

export function createSearchStore(initState: SearchState) {
  return createStore<SearchStore>(set => ({
    ...initState,
    setNextPage: () => {
      set(({ pagination }) => ({
        pagination: {
          ...pagination,
          offset: pagination.offset + DEFAULT_RESULTS_PER_PAGE,
        },
      }))
    },
    setPreviousPage: () => {
      set(({ pagination }) => ({
        pagination: {
          ...pagination,
          offset: Math.max(0, pagination.offset - DEFAULT_RESULTS_PER_PAGE),
        },
      }))
    },
    setPage: (pageNumber) => {
      set(({ pagination }) => ({
        pagination: {
          ...pagination,
          offset: Math.max(0, Number(pageNumber) * pagination.limit - DEFAULT_RESULTS_PER_PAGE),
        },
      }))
    },
    setSortingBy: (by) => {
      set(({ pagination }) => ({
        pagination: {
          ...pagination,
          offset: 0,
        },
        sorting: { by },
      }))
    },
    setFilterSelection: (filterType, value) => {
      set(({ filterSelection, pagination, aiFilterSelection }) => {
        const removingValue = !value || (Array.isArray(value) && !value.length)
        return {
          pagination: {
            ...pagination,
            offset: 0,
          },
          filterSelection: {
            ...filterSelection,
            [filterType]: value,
          },
          ...(removingValue && {
            aiFilterSelection: {
              ...aiFilterSelection,
              [filterType]: value,
            },
          }),
        }
      })
    },
    setParticipantsAndSorting: (participants, sortingBy) => {
      set(({ filterSelection, pagination, aiFilterSelection }) => {
        const removingValue = !participants || (Array.isArray(participants) && !participants.length)
        return {
          pagination: {
            ...pagination,
            offset: 0,
          },
          sorting: { by: sortingBy },
          filterSelection: {
            ...filterSelection,
            Participants: participants,
          },
          ...(removingValue && {
            aiFilterSelection: {
              ...aiFilterSelection,
              Participants: participants,
            },
          }),
        }
      })
    },
    resetFilterValues: <K extends SearchFilterType>(filterTypesToReset: K[]) => {
      set(({ filterSelection, pagination }) => ({
        pagination: {
          ...pagination,
          offset: 0,
        },
        filterSelection: Object.entries(filterSelection).reduce((acc, [filterType, filterValue]) => {
          if (filterTypesToReset.includes(filterType as K))
            return acc

          return {
            ...acc,
            [filterType]: filterValue,
          }
        }, {}),
        userQuery: '',
        aiFilterSelection: {},
      }))
    },
    setUserQuery: (userQuery) => {
      set(() => ({
        userQuery,
      }))
    },
    setIsAIFetching: (isAIFetching) => {
      set(() => ({
        isAIFetching,
      }))
    },
    setIsAIError: (isAIError) => {
      set(() => ({
        isAIError,
      }))
    },
    clearInitialAccommodationIds: () => {
      set(() => ({
        initialAccommodationIds: [],
      }))
    },
  }))
}

export const searchStore = createSearchStore(initialState)
